import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { Loader2, Mail, X } from 'lucide-react';
import { getEmailRecipients } from '../../Services/EmailAdminService';
import { EmailRecipient } from '../../Types/EmailAdminType';

export interface EmailRecipientPickerRef {
  /** Commits any valid, typed e-mail address into the value list and returns the updated list. */
  commit: () => string[];
}

interface Props {
  value: string[];
  onChange: (emails: string[]) => void;
  placeholder?: string;
  type?: 'CANDIDATE' | 'RECRUITER' | '';
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Chip input that resolves recipients two ways: free text (any valid address, added on Enter,
 * comma, blur or an explicit commit) and a searchable dropdown backed by the users table so admins
 * can pick known candidates and recruiters without copying addresses around.
 */
const EmailRecipientPicker = forwardRef<EmailRecipientPickerRef, Props>(function EmailRecipientPicker(
  { value, onChange, placeholder, type = '' },
  ref
) {
  const [input, setInput] = useState('');
  const [options, setOptions] = useState<EmailRecipient[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const term = input.trim();
    if (term.length < 2) {
      setOptions([]);
      return;
    }
    const handle = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await getEmailRecipients({
          search: term,
          type: type || undefined,
          page: 0,
          size: 8,
        });
        if (res.success && res.data) setOptions(res.data.content ?? []);
      } catch {
        setOptions([]);
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => clearTimeout(handle);
  }, [input, type]);

  useEffect(() => {
    const onClickOutside = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  const addEmail = (email: string): string[] => {
    const clean = email.trim().toLowerCase();
    if (!clean || !EMAIL_REGEX.test(clean) || value.includes(clean)) {
      return value;
    }
    const next = [...value, clean];
    onChange(next);
    setInput('');
    setOptions([]);
    return next;
  };

  const removeEmail = (email: string) => onChange(value.filter((e) => e !== email));

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      if (open && options.length > 0 && input.trim().length >= 2) {
        addEmail(options[0].email);
      } else {
        addEmail(input);
      }
    } else if (e.key === 'Backspace' && !input && value.length > 0) {
      removeEmail(value[value.length - 1]);
    }
  };

  const commit = (): string[] => {
    if (input.trim() && EMAIL_REGEX.test(input.trim())) {
      return addEmail(input);
    }
    return value;
  };

  const handleBlur = () => {
    // If the user typed a valid address and left the field, add it so clicking Send works.
    commit();
  };

  useImperativeHandle(ref, () => ({ commit }), [input, value]);

  return (
    <div ref={boxRef} className="relative">
      <div className="flex min-h-[42px] flex-wrap items-center gap-1.5 rounded-xl border border-slate-800 bg-slate-950/60 px-2.5 py-2 transition focus-within:border-cyan-500/50">
        {value.map((email) => (
          <span
            key={email}
            className="inline-flex items-center gap-1 rounded-lg border border-cyan-500/30 bg-cyan-500/10 px-2 py-1 text-[11px] font-bold text-cyan-300"
          >
            {email}
            <button type="button" onClick={() => removeEmail(email)} className="text-cyan-400/70 hover:text-cyan-200">
              <X size={11} />
            </button>
          </span>
        ))}
        <input
          value={input}
          onChange={(e) => {
            setInput(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          placeholder={value.length === 0 ? placeholder || 'Add recipients…' : ''}
          className="min-w-[120px] flex-1 bg-transparent px-1 py-1 text-sm font-semibold text-slate-100 outline-none placeholder:text-slate-600"
        />
        {loading && <Loader2 size={14} className="animate-spin text-cyan-500" />}
      </div>

      {open && input.trim().length >= 2 && (
        <div className="absolute z-30 mt-1 max-h-64 w-full overflow-auto rounded-xl border border-slate-800 bg-slate-900 shadow-2xl">
          {options.length === 0 && !loading && (
            <div className="px-3 py-2.5 text-xs font-semibold text-slate-500">
              No saved recipients. Press Enter to add “{input.trim()}”.
            </div>
          )}
          {options.map((r) => (
            <button
              key={r.id}
              type="button"
              onMouseDown={(e) => {
                // Use onMouseDown so the button is activated before the input blur fires.
                e.preventDefault();
                addEmail(r.email);
                setOpen(false);
              }}
              className="flex w-full items-center gap-2 px-3 py-2.5 text-left transition hover:bg-slate-800"
            >
              <Mail size={14} className="text-slate-500" />
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-bold text-slate-100">{r.name}</span>
                <span className="block truncate text-[11px] font-semibold text-slate-500">{r.email}</span>
              </span>
              <span className="rounded-md border border-slate-700 bg-slate-950/60 px-1.5 py-0.5 text-[9px] font-black uppercase tracking-wider text-slate-400">
                {r.role}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
});

export default EmailRecipientPicker;
